import { useEffect, useState, useMemo } from "react";
import { Schedule } from "@mui/icons-material";
import { Box, Typography } from "@mui/material";

import {
  useGetSDRImage,
  useGetSDRArtifacts,
} from "../services/DocumentServices";
import { getCachedBlobUrl, cacheBlobUrl } from "../services/ImageBlobCache";

/** Check if a file is an XER (Primavera P6 schedule) file */
const isXerFile = (filename: string | undefined | null): boolean => {
  if (!filename) return false;
  return filename.toLowerCase().endsWith(".xer");
};

/** Placeholder component for files that don't have image previews */
const ScheduleFilePlaceholder = () => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      padding: 2,
      color: "text.secondary",
      height: "100%",
      minHeight: 80,
    }}
  >
    <Schedule sx={{ fontSize: 32, mb: 0.5, opacity: 0.7 }} />
    <Typography variant="caption" sx={{ opacity: 0.8 }}>
      Schedule File
    </Typography>
  </Box>
);

type SDRImageProps = {
  sdrID: number;
};

export const SDRImage = ({ sdrID }: SDRImageProps) => {
  // Validate sdrID early
  const isValidSdrID = sdrID && typeof sdrID === "number" && !isNaN(sdrID);

  // Get artifact ID for cache lookup
  const { data: artifacts } = useGetSDRArtifacts(isValidSdrID ? sdrID : 0);
  const artifactId =
    artifacts && artifacts.length > 0 ? artifacts[0]?.id : null;

  // Check if this is an XER file (no image preview available)
  const isXer = useMemo(() => {
    if (!artifacts || artifacts.length === 0) return false;
    const filename = artifacts[0]?.filename || artifacts[0]?.name;
    return isXerFile(filename);
  }, [artifacts]);

  const {
    data: sdrImageData,
    isLoading,
    isError,
    error,
  } = useGetSDRImage(isValidSdrID ? sdrID : 0);

  const [imageUrl, setImageUrl] = useState("");

  // Reset imageUrl when sdrID or artifactId changes - CRITICAL for sorting/pagination
  useEffect(() => {
    // When sdrID/artifactId changes, check cache for the new artifact
    if (artifactId) {
      const cached = getCachedBlobUrl(artifactId);
      setImageUrl(cached || "");
    } else {
      setImageUrl("");
    }
  }, [sdrID, artifactId]);

  // Populate imageUrl from fetched data if not already cached
  useEffect(() => {
    if (!imageUrl && !isLoading && !isError && sdrImageData && artifactId) {
      // Create blob and cache it - cache manages URL lifecycle
      const blob = new Blob([sdrImageData], { type: "image/png" });
      const url = cacheBlobUrl(artifactId, blob);
      setImageUrl(url);
    }
  }, [isLoading, isError, sdrImageData, artifactId, imageUrl]);

  if (!isValidSdrID) {
    return (
      <div style={{ padding: "8px", color: "#666", fontSize: "12px" }}>
        Invalid image ID
      </div>
    );
  }

  // XER files don't have image previews - show schedule placeholder
  if (isXer) {
    return <ScheduleFilePlaceholder />;
  }

  // Still loading if: image query in progress, or image data arrived but blob URL not yet created
  if (isLoading || (sdrImageData && !imageUrl)) {
    return (
      <div style={{ padding: "8px", color: "#666", fontSize: "12px" }}>
        Loading...
      </div>
    );
  }

  // Handle 404 and other errors more gracefully
  if (isError || error) {
    const errorStatus = (error as unknown as { response?: { status?: number } })
      ?.response?.status;

    // Don't show error for 404 - just show no image available
    if (errorStatus === 404) {
      return (
        <div style={{ padding: "8px", color: "#666", fontSize: "12px" }}>
          No image available
        </div>
      );
    }

    return (
      <div style={{ padding: "8px", color: "#666", fontSize: "12px" }}>
        No image available
      </div>
    );
  }

  if (!imageUrl) {
    return (
      <div style={{ padding: "8px", color: "#666", fontSize: "12px" }}>
        No image available
      </div>
    );
  }

  return (
    <div style={{ width: "100%", height: "100%", margin: "5px" }}>
      <img
        src={imageUrl}
        alt=""
        style={{ width: "100%", height: "100%", objectFit: "contain" }}
        data-testid={`sdr-image-${sdrID}`}
      />
    </div>
  );
};
